import { Tooltip } from '@mantine/core'
import { arrayOfObjectsSearch } from '@semios/app-platform-common'
import type { TAdminPermission } from 'App/Map/Admin/utils/useAdminEntites'
import { HierarchicalSelection } from 'components/HierarchicalSelection/HierarchicalSelection'
import { IconInfoLight } from 'components/icons/IconInfoLight'
import { translate } from 'i18n/i18n'
import React, { useMemo, useState } from 'react'
import { colors } from 'settings/colors'
import { sortByKey } from 'utils/sortByKey'

const rowWrapperStyle = {
  alignItems: 'center',
  border: '1px solid rgb(233, 233, 233)',
  display: 'flex',
  padding: '4px 10px',
  transition: '0.15s ease all',
}

const selectedRowStyle = {
  background: colors.primary,
  border: `1px solid ${colors.primary}`,
  color: colors.white,
}

const regularRowStyle = {
  '&:hover': {
    background: colors.subtleBackground,
  },
}

const PermissionRow = ({
  permission,
  count,
  isSelected,
  setIsSelected,
  style,
}: {
  permission: TAdminPermission
  count: number
  isSelected: boolean
  setIsSelected: (isSelected: boolean, permissionId: TAdminPermission['id']) => void
  style?: React.CSSProperties
}) => {
  const extraRowStyle = isSelected ? selectedRowStyle : regularRowStyle

  return (
    <div
      css={{
        ...rowWrapperStyle,
        ...extraRowStyle,
        cursor: 'pointer',
        ...style,
      }}
      onClick={() => setIsSelected(!isSelected, permission.id)}
    >
      <div css={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        <Tooltip label={permission.description} withArrow position={'right'}>
          <span css={{ cursor: 'help', paddingRight: '5px' }}>
            <IconInfoLight />
          </span>
        </Tooltip>
        {count ? (
          <span>
            <strong>{`(${count}) ${permission.id}`}</strong>
          </span>
        ) : (
          permission.id
        )}
      </div>
    </div>
  )
}

export const PermissionsPicker = ({
  permissions,
  permissionIdToCountOfEntities,
  selectedPermission,
  setSelectedPermission,
  width,
}: {
  permissions: TAdminPermission[]
  permissionIdToCountOfEntities: Record<string, number>
  selectedPermission: TAdminPermission['id'] | null
  setSelectedPermission: (permission: TAdminPermission['id'] | null) => void
  width: string
}) => {
  const [searchString, setSearchString] = useState<string | undefined>()

  const filteredPermissions = useMemo(() => {
    const searchedPermissions = arrayOfObjectsSearch(permissions, searchString ?? '', ['id'])

    return searchedPermissions.sort(sortByKey('id'))
  }, [searchString, permissions])

  return (
    <HierarchicalSelection
      width={width}
      title={translate.phrases.banyanApp('Permissions')}
      placeholder={translate.phrases.banyanApp('Search for a permission')}
      searchString={searchString ?? ''}
      setSearchString={setSearchString}
      items={filteredPermissions}
      renderItem={({ index, style, data }) => (
        <PermissionRow
          permission={data[index]}
          count={permissionIdToCountOfEntities[data[index].id] ?? 0}
          isSelected={data[index].id === selectedPermission}
          style={style}
          setIsSelected={(isSelected, permissionId) =>
            setSelectedPermission(isSelected ? permissionId : null)
          }
        />
      )}
    />
  )
}
